import { isEmpty } from 'lodash';
import { createSelector } from 'reselect';

import { createKeySelector } from 'Lib/reduxUtils';

import type { Product } from 'Store/entities/products.types';

import type { IncentivesState } from './types';

/**
 * Gets profile namespace from the state
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {IncentivesState} profile state
 */
export const selectIncentives = createKeySelector('incentives');

/**
 * @param  {GlobalStateType} state - top-level state
 * @return is eligibility check complete
 */
export const getIsIncentiveEligibilityCheckComplete = createSelector(
  selectIncentives,
  (incentives: IncentivesState): boolean => incentives.eligibilityCheckComplete
);

/**
 * @param  {GlobalStateType} state - top-level state
 * @return the status of the incentive eligibility
 */
export const getIsIncentiveEligible = createSelector(
  selectIncentives,
  (incentives: IncentivesState) => incentives.incentiveDetails?.eligible ?? false
);

/**
 * @param  {GlobalStateType} state - top-level state
 * @return the name of the incentive eligibility
 */
export const getIncentiveName = createSelector(
  selectIncentives,
  (incentives: IncentivesState) => incentives.incentiveDetails?.code ?? ''
);

/**
 * @param  {GlobalStateType} state - top-level state
 * @return the campaign id of the incentive
 */
export const getIncentiveCampaignId = createSelector(
  selectIncentives,
  (incentives: IncentivesState) => incentives.incentiveDetails?.campaignId ?? null
);

/**
 * @param  {GlobalStateType} state - top-level state
 * @return the campaign name of the incentive
 */
export const getIncentiveCampaignName = createSelector(
  selectIncentives,
  (incentives: IncentivesState) => incentives.incentiveDetails?.campaignName ?? null
);

/**
 * Gets if the campaign is of type intro price
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {boolean} unit
 */
export const getIsIntroPriceIncentiveCampaign = createSelector(
  selectIncentives,
  (incentives: IncentivesState) => incentives.incentiveDetails?.isIntroPrice ?? false
);

/**
 * @param  {GlobalStateType} state - top-level state
 * @return if the campaign is of type free trial
 */
export const getIsFreeTrialIncentiveCampaign = createSelector(
  selectIncentives,
  (incentives: IncentivesState) => incentives.incentiveDetails?.isIntroPrice === false
);

/**
 * @param  {GlobalStateType} state - top-level state
 * @return is cc required
 */
export const getIncentiveCCRequired = createSelector(
  selectIncentives,
  (incentives: IncentivesState) => incentives.incentiveDetails?.ccRequired ?? false
);

/**
 * @param  {GlobalStateType} state - top-level state
 * @return the purchaseType of the incentive eligibility
 */
export const getIncentivePurchaseType = createSelector(
  selectIncentives,
  (incentives: IncentivesState) => incentives.incentiveDetails?.offers?.[0]?.purchaseType ?? null
);

/**
 * @param  {GlobalStateType} state - top-level state
 * @return the productType of the incentive eligibility
 */
export const getIncentiveProductType = createSelector(
  selectIncentives,
  (incentives: IncentivesState) => incentives.incentiveDetails?.offers?.[0]?.productType ?? null
);

/**
 * @param  {GlobalStateType} state - top-level state
 * @return the amount of the incentive product
 */
export const getIncentiveProductAmount = createSelector(
  selectIncentives,
  (incentives: IncentivesState) => incentives.incentiveDetails?.offers?.[0]?.amount ?? null
);

/**
 * @param  {GlobalStateType} state - top-level state
 * @return the unit of the incentive product
 */
export const getIncentiveProductUnit = createSelector(
  selectIncentives,
  (incentives: IncentivesState) => incentives.incentiveDetails?.offers?.[0]?.unit ?? null
);

/**
 * @param  {GlobalStateType} state - top-level state
 * @return incentive error
 */
export const getIncentiveError = createSelector(
  selectIncentives,
  (incentives: IncentivesState) => incentives.incentiveError
);

/**
 * @param  {GlobalStateType} state - top-level state
 * @return incentive failure
 */
export const getIsEligibilityFailure = createSelector(
  selectIncentives,
  (incentives: IncentivesState) => incentives.isEligibilityFailure
);

/**
 * @param  {GlobalStateType} state - top-level state
 * @return if incentive redemption is a failure
 */
export const getIsRedemptionFailure = createSelector(
  selectIncentives,
  (incentives: IncentivesState) => incentives.isRedemptionFailure
);

/**
 * Gets the incentive promo source
 *
 * @param  {GlobalStateType} state - top-level state
 * @return get where the promo was entered
 */
export const getIncentivePromoSource = createSelector(
  selectIncentives,
  (incentives: IncentivesState) => incentives.promoSource
);

/**
 * @param  {GlobalStateType} state - top-level state
 * @return is redemption successful
 */
export const getIncentiveSuccess = createSelector(
  selectIncentives,
  (incentives: IncentivesState): boolean => incentives.incentiveSuccess
);

/**
 * @param  {GlobalStateType} state - top-level state
 * @return loading status of the incentive eligibility check; true if eligibility request is loading
 */
export const getIncentiveIsLoading = createSelector(
  selectIncentives,
  (incentives: IncentivesState): boolean => incentives.incentiveIsLoading
);

/**
 * @param  {GlobalStateType} state - top-level state
 * @return introprice incentive product
 */
export const getIncentiveIntroPriceProduct = createSelector(
  selectIncentives,
  (incentives: IncentivesState): Product | null => incentives.incentiveIntroPriceProduct
);

/**
 * @param  {GlobalStateType} state - top-level state
 * @return is introprice incentive product available
 */
export const getHasIncentiveIntroPriceProduct = createSelector(
  getIncentiveIntroPriceProduct,
  (product): boolean => !isEmpty(product)
);

/**
 * @param  {GlobalStateType} state - top-level state
 * @return productType of the gift card
 */
export const getGiftCardProductType = createSelector(
  selectIncentives,
  (incentives: IncentivesState) => incentives.giftCardDetails?.product_type ?? null
);

/**
 * @param  {GlobalStateType} state - top-level state
 * @return is gift card successful
 */
export const getGiftCardSuccess = createSelector(
  selectIncentives,
  (incentives: IncentivesState): boolean => incentives.giftCardSuccess
);
